import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Edit2 } from 'lucide-react';
import AdvancedPriceChartWidget from '../DashboardBuilder/AdvancedPriceChartWidget';
import CategoryAccordion from '../DashboardBuilder/CategoryAccordion';
import IndicatorSettingsModal from '../DashboardBuilder/IndicatorSettingsModal';
import { formatIndicatorDisplay, mergeCustomIndicators } from '../../utils/indicatorCategories';
import { useCustomIndicators } from '../../contexts/CustomIndicatorContext';
import { INDICATOR_DEFAULTS } from '../../utils/indicatorDefaults';

// localStorage keys (separate from OptionsPremiumChartModal)
const STORAGE_INDICATORS = 'researchPriceChart_indicators';
const STORAGE_BOLLINGER = 'researchPriceChart_showBollinger';
const STORAGE_SETTINGS = 'researchPriceChart_indicatorSettings';

export default function PriceChartModal({ isOpen, onClose, ticker }) {
  const [dateRange, setDateRange] = useState('1y');

  const [indicators, setIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_INDICATORS);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [showBollinger, setShowBollinger] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_BOLLINGER) === 'true';
    } catch {
      return false;
    }
  });

  const [showIndicatorDropdown, setShowIndicatorDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [hoveredIndicator, setHoveredIndicator] = useState(null);

  const [indicatorSettings, setIndicatorSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_SETTINGS);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const dropdownRef = useRef(null);
  const { customIndicators } = useCustomIndicators();
  const mergedCategories = mergeCustomIndicators(customIndicators);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowIndicatorDropdown(false);
      }
    };
    if (showIndicatorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showIndicatorDropdown]);

  // Persist to localStorage
  useEffect(() => {
    try { localStorage.setItem(STORAGE_INDICATORS, JSON.stringify(indicators)); } catch {}
  }, [indicators]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_BOLLINGER, String(showBollinger)); } catch {}
  }, [showBollinger]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_SETTINGS, JSON.stringify(indicatorSettings)); } catch {}
  }, [indicatorSettings]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleIndicatorToggle = (indicatorValue) => {
    const isCustom = indicatorValue.startsWith('custom_');
    let baseIndicator = indicatorValue;
    let customSettings = null;

    if (isCustom) {
      const customId = parseInt(indicatorValue.replace('custom_', ''));
      const customIndicator = customIndicators.find(ci => ci.id === customId);
      if (customIndicator) {
        baseIndicator = customIndicator.base_indicator;
        customSettings = customIndicator.settings;
      }
    }

    setIndicators(prev => {
      if (prev.includes(baseIndicator)) {
        return prev.filter(i => i !== baseIndicator);
      }
      return [...prev, baseIndicator];
    });

    if (isCustom && customSettings && !indicators.includes(baseIndicator)) {
      setIndicatorSettings(prev => ({ ...prev, [baseIndicator]: customSettings }));
    }

    if (baseIndicator === 'bollinger' && !indicators.includes('bollinger')) {
      setShowBollinger(true);
    }
  };

  const handleOpenSettings = (indicator) => {
    setSelectedIndicator(indicator);
    setShowSettingsModal(true);
    setShowIndicatorDropdown(false);
  };

  const handleSaveSettings = async (indicator, settings) => {
    setIndicatorSettings(prev => ({ ...prev, [indicator]: settings }));
  };

  // Build indicatorConfig for AdvancedPriceChartWidget
  const indicatorConfig =
    indicators.length > 0 && Object.keys(indicatorSettings).some(key => indicators.includes(key))
      ? indicators.reduce((config, ind) => {
          const defaultSettings = INDICATOR_DEFAULTS[ind] || {};
          const customSettings = indicatorSettings[ind] || {};
          config[ind] = {
            ...defaultSettings,
            ...customSettings,
            colors: {
              ...defaultSettings.colors,
              ...customSettings.colors,
            },
          };
          return config;
        }, {})
      : null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
      style={{ zIndex: 9999 }}
      onClick={handleOverlayClick}
    >
      <div className="bg-surface rounded-lg w-full max-w-5xl h-[700px] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <span className="font-semibold text-text text-lg">{ticker}</span>

          <div className="flex items-center gap-3">
            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-background text-text text-xs px-2 py-1 rounded border border-muted hover:border-primary transition"
            >
              <option value="1m">1M</option>
              <option value="3m">3M</option>
              <option value="6m">6M</option>
              <option value="1y">1Y</option>
              <option value="2y">2Y</option>
              <option value="5y">5Y</option>
            </select>

            {/* Indicators Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowIndicatorDropdown(!showIndicatorDropdown)}
                className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition flex items-center gap-1"
                title="Indicators"
              >
                <span>Indicators</span>
                {indicators.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-blue-600/30 text-blue-400 rounded text-[10px]">
                    {indicators.length}
                  </span>
                )}
                <ChevronDown className="w-3 h-3" />
              </button>

              {showIndicatorDropdown && (
                <div
                  className="absolute top-full right-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50 min-w-64 max-h-80 overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Active Indicators */}
                  <div className="px-3 py-2 border-b border-gray-700">
                    <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Active Indicators</p>
                    <div className="space-y-1">
                      {indicators.length > 0 ? (
                        indicators.map(ind => (
                          <div
                            key={ind}
                            className="flex items-center justify-between text-sm text-gray-200 px-2 py-1 rounded hover:bg-gray-700/50 group"
                            onMouseEnter={() => setHoveredIndicator(ind)}
                            onMouseLeave={() => setHoveredIndicator(null)}
                          >
                            <button
                              onClick={() => handleOpenSettings(ind)}
                              className="flex-1 text-left hover:text-blue-400 transition-colors"
                              title="Click to edit settings"
                            >
                              {formatIndicatorDisplay(ind, indicatorSettings[ind])}
                            </button>
                            <div className="flex items-center gap-1">
                              {hoveredIndicator === ind && (
                                <button
                                  onClick={() => handleOpenSettings(ind)}
                                  className="text-gray-400 hover:text-white transition-colors px-1"
                                  title="Edit settings"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => handleIndicatorToggle(ind)}
                                className="text-gray-400 hover:text-red-400 text-xs"
                                title="Remove indicator"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">None</span>
                      )}
                    </div>
                  </div>

                  {/* Bollinger Toggle */}
                  {indicators.includes('bollinger') && (
                    <div className="px-3 py-2 border-b border-gray-700">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showBollinger}
                          onChange={(e) => setShowBollinger(e.target.checked)}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-gray-300">Show Bollinger Bands</span>
                      </label>
                    </div>
                  )}

                  {/* Available Indicators */}
                  <div className="px-3 py-2">
                    <p className="text-xs text-gray-400 mb-2">Add Indicators:</p>
                    <CategoryAccordion
                      categories={mergedCategories}
                      selectedItems={indicators}
                      onToggle={handleIndicatorToggle}
                      onEdit={handleOpenSettings}
                      displayMode="compact"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition p-1 rounded hover:bg-gray-700"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Chart Area */}
        <div className="flex-1 overflow-hidden">
          <AdvancedPriceChartWidget
            ticker={ticker}
            dateRange={dateRange}
            indicators={indicators}
            indicatorConfig={indicatorConfig}
            isLinked={false}
            showBollinger={showBollinger}
            setShowBollinger={setShowBollinger}
          />
        </div>
      </div>

      {/* Indicator Settings Modal */}
      {showSettingsModal && selectedIndicator && (
        <IndicatorSettingsModal
          isOpen={showSettingsModal}
          onClose={() => {
            setShowSettingsModal(false);
            setSelectedIndicator(null);
          }}
          onSave={handleSaveSettings}
          indicator={selectedIndicator}
          currentSettings={indicatorSettings[selectedIndicator] || {}}
        />
      )}
    </div>
  );
}
